export const queryKeys = {
  auth: {
    all: ['auth'],
    // Rule 68: sessions list is server-paginated/sorted, so the key carries
    // params. `sessionsAll` is the 2-element prefix for invalidating every
    // page/sort variant at once (TanStack partial-match).
    sessions: (params = {}) => ['auth', 'sessions', params],
    sessionsAll: ['auth', 'sessions'],
  },
  notifications: {
    all: ['notifications'],
    list: (params = {}) => ['notifications', 'list', params],
  },
  // Authorized Devices (Rule 73)
  devices: {
    all: ['devices'],
    list: () => ['devices', 'list'],
  },
  oauthClients: {
    all: ['oauth-clients'],
    list: (params = {}) => ['oauth-clients', 'list', params],
  },
  oauthUsers: {
    all: ['oauth-users'],
    list: (params = {}) => ['oauth-users', 'list', params],
    detail: (userId) => ['oauth-users', 'detail', userId],
    scopes: () => ['oauth-users', 'scopes-catalog'],
  },
  systemSettings: {
    all: ['system-settings'],
    list: () => ['system-settings', 'list'],
  },
  exampleBatchUploads: {
    all: ['example-batch-uploads'],
    list: (params = {}) => ['example-batch-uploads', 'list', params],
  },
  uploadShareTokens: {
    all: ['upload-share-tokens'],
    list: (params = {}) => ['upload-share-tokens', 'list', params],
    sourceFile: (id) => ['upload-share-tokens', 'source-file', id],
  },
  chat: {
    all: ['chat'],
    threadsAll: ['chat', 'threads'],
    threads: (params = {}) => ['chat', 'threads', params],
    messages: (threadId, params = {}) => ['chat', 'messages', threadId, params],
    participants: (threadId) => ['chat', 'participants', threadId],
    settings: () => ['chat', 'settings'],
    // Rule 74 v1.68 — per-user mute list (suppresses ringtone for muted callers).
    mutes: () => ['chat', 'mutes'],
    // Phase B — per-thread mute list (suppresses CHAT_NOTIFICATION for muted threads).
    threadMutes: () => ['chat', 'thread-mutes'],
    // Phase E — user block list (bidirectional cut for chat + 1:1 calls).
    blocks: () => ['chat', 'blocks'],
    // Phase B — single-thread View side-panel summary (peer/media/files/links/mute).
    threadView: (threadId) => ['chat', 'thread-view', threadId],
    // Phase C — pinned messages per thread.
    pinned: (threadId) => ['chat', 'pinned', threadId],
  },

  // Voice + Video Calls (Architecture Rule 74)
  calls: {
    all: ['calls'],
    detail: (callId) => ['calls', 'detail', callId],
    thread: (threadId, params = {}) => ['calls', 'thread', threadId, params],
    recordingUrl: (callId) => ['calls', 'recording-url', callId],
  },

  // ── CRM core (Rules 65 / 75 / 76) ────────────────────────────────────────
  // `*All` is the 1-element prefix used to invalidate every page/sort/filter
  // variant at once after a mutation (TanStack partial match).
  organizations: {
    all: ['organizations'],
    list: (params = {}) => ['organizations', 'list', params],
    detail: (id) => ['organizations', 'detail', id],
    timeline: (id, params = {}) => ['organizations', 'timeline', id, params],
  },
  contacts: {
    all: ['contacts'],
    list: (params = {}) => ['contacts', 'list', params],
    detail: (id) => ['contacts', 'detail', id],
    timeline: (id, params = {}) => ['contacts', 'timeline', id, params],
  },
  tags: {
    all: ['tags'],
    list: (params = {}) => ['tags', 'list', params],
  },
  leads: {
    all: ['leads'],
    list: (params = {}) => ['leads', 'list', params],
    detail: (id) => ['leads', 'detail', id],
    timeline: (id, params = {}) => ['leads', 'timeline', id, params],
    sources: () => ['leads', 'sources'],
  },
  opportunities: {
    all: ['opportunities'],
    list: (params = {}) => ['opportunities', 'list', params],
    detail: (id) => ['opportunities', 'detail', id],
    timeline: (id, params = {}) => ['opportunities', 'timeline', id, params],
  },
  pipelines: {
    all: ['pipelines'],
    list: () => ['pipelines', 'list'],
    lostReasons: () => ['pipelines', 'lost-reasons'],
  },
  // Rule 77 — the AGENT side of the website widget. The visitor side belongs to
  // the embeddable bundle and never touches this cache.
  liveChat: {
    all: ['livechat'],
    queue: () => ['livechat', 'queue'],
    session: (id) => ['livechat', 'session', id],
  },
  activities: {
    all: ['activities'],
    list: (params = {}) => ['activities', 'list', params],
    detail: (id) => ['activities', 'detail', id],
    // Deliberately its own key rather than a `list` variant: My Day is a
    // different question (three server-bucketed lists, always the caller's own
    // work), so it must not be invalidated or served by a list cache entry.
    myDay: (params = {}) => ['activities', 'my-day', params],
  },
  salesTeams: {
    all: ['sales-teams'],
    list: (params = {}) => ['sales-teams', 'list', params],
    members: (id) => ['sales-teams', 'members', id],
  },
  assignmentRules: {
    all: ['assignment-rules'],
    list: (params = {}) => ['assignment-rules', 'list', params],
  },

  // System-Administrator read-only viewers (Rule 68)
  auditLogs: {
    all: ['admin-audit-logs'],
    list: (params = {}) => ['admin-audit-logs', 'list', params],
    export: (id) => ['admin-audit-logs', 'export', id],
  },
  outboundHttpLogs: {
    all: ['admin-outbound-http-logs'],
    list: (params = {}) => ['admin-outbound-http-logs', 'list', params],
  },
  fieldHistory: {
    all: ['admin-field-history'],
    list: (params = {}) => ['admin-field-history', 'list', params],
  },
  loginAttempts: {
    all: ['admin-login-attempts'],
    list: (params = {}) => ['admin-login-attempts', 'list', params],
  },
  chatMessagesAdmin: {
    all: ['admin-chat-messages'],
    list: (params = {}) => ['admin-chat-messages', 'list', params],
    thread: (threadId, params = {}) => ['admin-chat-messages', 'thread', threadId, params],
  },
  webhookDeliveries: {
    all: ['admin-webhook-deliveries'],
    list: (params = {}) => ['admin-webhook-deliveries', 'list', params],
  },
  failedJobs: {
    all: ['admin-failed-jobs'],
    list: (params = {}) => ['admin-failed-jobs', 'list', params],
  },
};
